/**
 * Engine-aware SQL emit for the `json` filter operator.
 *
 * Shared between the renderer-side `filterWhereBuilder.ts` and the
 * main-process `electron/table-explorer/queryBuilder/queryUtils.ts` so both
 * paths produce equivalent JSON predicates.
 *
 * The `json` operator is two-level: a top-level `'json'` operator selects
 * this code path, then `jsonSubOperator` discriminates the actual predicate.
 * `path` carries the JSONPath (`$.address.city` or shorthand `address.city`),
 * `value` carries the comparand for sub-ops that need one, and
 * `jsonDeclaredOnText` triggers the engine's JSON-validity guard wrap
 * (MSSQL `ISJSON`, Oracle `IS JSON`, SQLite `json_valid`). PG / MySQL
 * never declare-on-text — both have native JSON types — so the guard
 * is a no-op for them.
 */

import { quoteMysqlText } from '../../../../core/utils/mysqlSqlLiteral';
import { withSqlServerNationalPathPrefix, withSqlServerNationalPrefix } from '../../../../core/utils/sqlServerTextLiteral';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition, JsonSubOperator } from '../../core/types/workspaceTypes';
import { containsLikeSql } from './containsLikeSql';

/** Sub-operators that need no guard wrap because they ARE the guard. */
const VALIDITY_SUB_OPS: ReadonlyArray<JsonSubOperator> = ['is_valid', 'is_not_valid'];

/**
 * Quote `value` as a SQL text literal (`'…'`), or emit `NULL` for nullish.
 *
 * Used at every JSON equality-style emit site (`path_eq`, `path_neq`,
 * `property_lookup`, their wildcard / recursive / aggregate variants) where
 * the left side is `extractTextSql(...)` or a lateral `valueExpr` — both of
 * which the engine-specific helpers now uniformly produce as TEXT. Forcing
 * a text-typed literal on the right side keeps the comparison text-vs-text
 * on every engine. Without this, a numeric `value` (e.g. an FK target ID
 * from the row picker) would emit as a bare numeric literal, which:
 *   - PG would reject (`text = integer` has no operator),
 *   - SQLite would silently mismatch (text 'X' ≠ integer X),
 *   - MySQL / MSSQL / Oracle would accept via implicit coercion.
 * Text-on-both-sides removes the engine divergence entirely.
 *
 * Numeric comparisons (`path_gt`, `path_lt`, aggregate `>=` / `<=` etc.) are
 * NOT routed through this helper — they pair `castNumericSql(textExpr)` with
 * a bare numeric value, keeping the comparison numeric-vs-numeric.
 *
 * On SQL Server a value with a character outside ASCII is written `N'…'` —
 * the JSON functions return nvarchar, whatever the column's type
 * (`sqlServerTextLiteral.ts`).
 */
export function escapeAsTextLiteral(value: unknown, engine?: DatabaseEngineId): string {
  if (value === null || value === undefined) return 'NULL';
  if (engine === 'mysql') return quoteMysqlText(String(value));
  const quoted = `'${String(value).replace(/'/g, "''")}'`;
  return engine === 'mssql' ? withSqlServerNationalPrefix(quoted, String(value)) : quoted;
}

/**
 * Build the comma-separated body of a SQL `IN (...)` list from a multiselect
 * enum value array. Each element is quoted as a text literal (mirrors
 * `escapeAsTextLiteral` so the comparison stays text-vs-text on every engine);
 * nullish elements emit bare `NULL`. Used by the `property_enum` / `any_enum` /
 * `all_enum` sub-ops when `condition.value` is an array.
 */
export function escapeAsTextInList(values: unknown[], engine?: DatabaseEngineId): string {
  return values
    .map((v) => escapeAsTextLiteral(v, engine))
    .join(', ');
}

/**
 * Normalize a user-entered path to canonical `$.a.b` JSONPath form.
 *
 * Accepts `$.a.b`, `a.b`, or `$`. Whitespace is trimmed; empty input
 * collapses to `$` (the document root).
 */
export function normalizeJsonPath(input: string | undefined | null): string {
  const trimmed = (input ?? '').trim();
  if (trimmed === '' || trimmed === '$') return '$';
  if (trimmed.startsWith('$')) return trimmed;
  return `$.${trimmed}`;
}

/**
 * Whether a JSONPath contains a `[*]` array wildcard. Slice 12 emits a
 * lateral-subquery shape (per-engine) for paths matching this — the
 * non-wildcard scalar-extract path can't represent "any element matches".
 * Recursive descent (`..`) is a separate concern (Slice 13).
 */
export function hasJsonPathWildcard(path: string | undefined | null): boolean {
  return (path ?? '').includes('[*]');
}

/**
 * Whether a JSONPath uses recursive descent (`..`). PG and Oracle support
 * this natively; MySQL / MSSQL / SQLite do not, and `buildJsonConditionSql`
 * throws `JsonFilterUnsupportedError` for those engines.
 */
export function hasJsonRecursiveDescent(path: string | undefined | null): boolean {
  return (path ?? '').includes('..');
}

/** Engines that emit native SQL for recursive-descent paths. */
const RECURSIVE_DESCENT_SUPPORTED_ENGINES: ReadonlyArray<DatabaseEngineId> = [
  'pgsql',
  'oracle',
];

/**
 * Thrown by `buildJsonConditionSql` when the requested path/sub-op pair
 * has no SQL emit on the active engine. Filter chips catch this and
 * surface an inline error to the user. Slice 13 introduces this for
 * recursive descent (`..`) on MySQL / MSSQL / SQLite; future slices may
 * extend it (e.g. filter expressions on engines without native filter).
 */
/**
 * Kinds of unsupported JSON filter emit. `recursive-descent` predates kusto
 * (MySQL/MSSQL/SQLite don't support `..`); `jsonpath-methods` is PostgreSQL-19
 * jsonpath methods (e.g. `.lower()`) on non-pgsql engines; the rest are
 * kusto-only — kusto has no JSON-column filter support at all (S1: no slice
 * implements it).
 */
export type JsonFilterUnsupportedReason =
  | 'recursive-descent'
  | 'jsonpath-methods'
  | 'path-exists'
  | 'extract-text'
  | 'cast-numeric'
  | 'is-empty'
  | 'is-not-empty'
  | 'is-valid'
  | 'wildcard-lateral'
  | 'wildcard-path-exists'
  | 'validity-guard';

export class JsonFilterUnsupportedError extends Error {
  readonly code = 'json-filter-unsupported';
  readonly engine: DatabaseEngineId;
  readonly reason: JsonFilterUnsupportedReason;
  constructor(engine: DatabaseEngineId, reason: JsonFilterUnsupportedReason, message: string) {
    super(message);
    this.name = 'JsonFilterUnsupportedError';
    this.engine = engine;
    this.reason = reason;
  }
}

/**
 * Translate a user-typed `..` path into the engine's native recursive-
 * descent syntax. PG SQL/JSON path uses `**` for any-depth descendant
 * (`$..foo` → `$.**.foo`); Oracle uses `..` natively, so no rewrite.
 */
function recursiveDescentPathForEngine(engine: DatabaseEngineId, path: string): string {
  const norm = normalizeJsonPath(path);
  if (engine === 'pgsql') {
    return norm.replace(/\.\./gu, '.**.');
  }
  return norm;
}

function recursiveDescentLateral(
  engine: DatabaseEngineId,
  quotedField: string,
  path: string,
): { from: string; valueExpr: string } {
  switch (engine) {
    case 'pgsql': {
      const lit = quotePathLiteral(recursiveDescentPathForEngine('pgsql', path));
      return {
        from: `jsonb_path_query(${quotedField}, ${lit}) AS p`,
        valueExpr: `(p #>> '{}')`,
      };
    }
    case 'oracle': {
      // `JSON_TABLE(col, '$..foo' COLUMNS (v ... PATH '$'))` walks every
      // descendant `foo` and exposes its scalar value as `jt.v`. Oracle
      // 19c+ supports `..` in `JSON_TABLE` row paths.
      const lit = quotePathLiteral(path);
      return {
        from: `JSON_TABLE(${quotedField}, ${lit} COLUMNS (v VARCHAR2(4000) PATH '$')) jt`,
        valueExpr: `jt.v`,
      };
    }
    case 'mysql':
    case 'mssql':
    case 'sqlite':
    case 'kusto':
    case 'azurestorage':
    case 'mock':
      throw new JsonFilterUnsupportedError(
        engine,
        'recursive-descent',
        'Recursive descent (..) not supported on this engine',
      );
  }
}

function recursiveDescentPathExistsSql(
  engine: DatabaseEngineId,
  quotedField: string,
  path: string,
  negate: boolean,
): string {
  switch (engine) {
    case 'pgsql': {
      const lit = quotePathLiteral(recursiveDescentPathForEngine('pgsql', path));
      return negate
        ? `NOT jsonb_path_exists(${quotedField}, ${lit})`
        : `jsonb_path_exists(${quotedField}, ${lit})`;
    }
    case 'oracle': {
      const lit = quotePathLiteral(path);
      return negate
        ? `NOT JSON_EXISTS(${quotedField}, ${lit})`
        : `JSON_EXISTS(${quotedField}, ${lit})`;
    }
    case 'mysql':
    case 'mssql':
    case 'sqlite':
    case 'kusto':
    case 'azurestorage':
    case 'mock':
      throw new JsonFilterUnsupportedError(
        engine,
        'recursive-descent',
        'Recursive descent (..) not supported on this engine',
      );
  }
}

/**
 * Split a wildcard path at its FIRST `[*]` into a row path (everything
 * before) and a tail path (everything after, re-rooted at `$`).
 *
 * - `$.items[*].name` → `{ rowPath: '$.items', tailPath: '$.name' }`
 * - `$.items[*]`      → `{ rowPath: '$.items', tailPath: '$' }`
 * - `$[*].id`         → `{ rowPath: '$',       tailPath: '$.id' }`
 *
 * Multi-wildcard paths (e.g. `$.a[*].b[*].c`) leave subsequent `[*]`
 * tokens in the tail path; engines that natively support multi-wildcard
 * JSONPath (PG, MySQL `JSON_TABLE`, Oracle `JSON_TABLE`) honor them, but
 * MSSQL `OPENJSON` and SQLite `json_each` only walk one level — calling
 * code uses the full path for the natively-multi engines and the split
 * pair for the lateral-walk engines.
 */
export function splitFirstWildcard(
  path: string,
): { rowPath: string; tailPath: string } {
  const norm = normalizeJsonPath(path);
  const idx = norm.indexOf('[*]');
  if (idx === -1) return { rowPath: norm, tailPath: '$' };
  const before = norm.substring(0, idx);
  const after = norm.substring(idx + 3);
  const rowPath = before === '' ? '$' : before;
  let tailPath: string;
  if (after === '') tailPath = '$';
  else if (after.startsWith('.') || after.startsWith('[')) tailPath = `$${after}`;
  else tailPath = `$.${after}`;
  return { rowPath, tailPath };
}

/**
 * Convert a `$.a.b` JSONPath into a Postgres text-array path literal
 * suitable for `#>` / `#>>`. `$.a.b` → `{a,b}`. Array-index segments
 * (`$.items[0].name` → `{items,0,name}`) are split out into integer
 * elements so PG indexes the array element rather than treating
 * `items[0]` as a literal key. Backslash-escapes any commas, braces,
 * double-quotes, or backslashes inside individual segments so a
 * malicious path can't break out of the array literal.
 *
 * TODO: only the trailing-`[N]` form is recognized. Mixed forms like
 * `[0][1]`, `*`, or filter expressions fall through and get escaped
 * literally — those are out of scope for v1.
 */
function jsonPathToPgArray(path: string): string {
  const norm = normalizeJsonPath(path);
  const body = norm.replace(/^\$\.?/u, '');
  if (body === '') return '{}';
  const rawSegments = body.split('.');
  const expanded: string[] = [];
  // Match a segment of the form `name[N]` (single trailing array index).
  const arraySegmentRe = /^([^[\]]*)\[(\d+)\]$/u;
  for (const seg of rawSegments) {
    const m = seg.match(arraySegmentRe);
    if (m) {
      const [, name, idx] = m;
      if (name.length > 0) expanded.push(name);
      expanded.push(idx);
    } else {
      expanded.push(seg);
    }
  }
  const escaped = expanded.map((s) => s.replace(/[\\",{}]/gu, (m) => `\\${m}`));
  return `{${escaped.join(',')}}`;
}

/**
 * SQL string literal for a path passed to engine JSON functions. SQL Server
 * writes a path outside ASCII `N'…'` (`withSqlServerNationalPathPrefix`).
 */
function quotePathLiteral(path: string, engine?: DatabaseEngineId): string {
  const norm = normalizeJsonPath(path);
  return engine === 'mssql' ? withSqlServerNationalPathPrefix(escapeAsTextLiteral(norm), norm) : escapeAsTextLiteral(norm, engine);
}

/**
 * Escape a value for embedding inside a jsonpath *double-quoted* string
 * literal (`"…"`), following JSON string rules: backslashes first, then
 * double-quotes (order matters — escaping `"` first would then double the
 * `\` we just added). The result is embedded inside the SQL string literal
 * produced by `jsonpathLowerEqLiteral`.
 */
function escapeJsonpathStringLiteral(value: string): string {
  return value.replace(/\\/gu, '\\\\').replace(/"/gu, '\\"');
}

/**
 * SQL string literal for a PG19 case-insensitive jsonpath filter of the form
 * `<path> ? (@.lower() == "<loweredValue>")` (PG19-FACTS f1/f2/f3). Two nested
 * quoting layers: `loweredValue` is escaped for the jsonpath double-quoted
 * string, then the whole jsonpath expression is wrapped as a SQL literal with
 * single quotes doubled. The value is lowercased by the caller so the
 * `.lower()`-on-both-sides comparison is case-insensitive; `.lower()` on a
 * non-string leaf returns false with no error in default lax mode (f3), so the
 * plain 2-arg `jsonb_path_exists` form is safe on heterogeneous rows.
 */
function jsonpathLowerEqLiteral(path: string, loweredValue: string): string {
  const jsonpath = `${normalizeJsonPath(path)} ? (@.lower() == "${escapeJsonpathStringLiteral(loweredValue)}")`;
  return `'${jsonpath.replace(/'/gu, "''")}'`;
}

/**
 * Split a non-wildcard JSONPath into its parent path and last segment key.
 *
 * - `$.a`           → parent `$`,           key `a`
 * - `$.a.b`         → parent `$.a`,         key `b`
 * - `$.a[0]`        → parent `$.a`,         key `0`
 * - `$.items[0].id` → parent `$.items[0]`,  key `id`
 * - `$[0]`          → parent `$`,           key `0`
 * - `$.a."東京"`     → parent `$.a`,         key `東京` (unquoted)
 *
 * Pre: `path` is normalized (`normalizeJsonPath`) and is NOT `$` — callers
 * branch on `path === '$'` before invoking. Wildcards (`[*]`) and recursive
 * descent (`..`) are out of scope here; they have their own per-engine
 * emit paths.
 *
 * Used by the MSSQL `pathExistsSql` branch to enumerate the parent's keys
 * via `OPENJSON` — the only T-SQL 2019-compatible way to distinguish
 * "key with JSON null value" from "key missing" (`JSON_QUERY` / `JSON_VALUE`
 * both return SQL NULL for both shapes).
 */
function splitJsonPathLastSegment(
  normPath: string,
): { parentPath: string; lastKey: string } {
  // A quoted key (`$.a."東京"` — SQL Server needs the quotes for a key outside
  // ASCII): OPENJSON's `[key]` holds the name without them.
  const quoted = /\."((?:[^"\\]|\\.)*)"$/.exec(normPath);
  if (quoted) {
    const parentPath = normPath.slice(0, quoted.index);
    return { parentPath: parentPath === '' ? '$' : parentPath, lastKey: quoted[1]!.replace(/\\(.)/g, '$1') };
  }
  const lastBracket = normPath.lastIndexOf('[');
  const lastDot = normPath.lastIndexOf('.');
  if (lastBracket > lastDot) {
    const closeIdx = normPath.lastIndexOf(']');
    const lastKey = normPath.substring(lastBracket + 1, closeIdx);
    const parentPath = normPath.substring(0, lastBracket);
    return { parentPath: parentPath === '' ? '$' : parentPath, lastKey };
  }
  const parentPath = normPath.substring(0, lastDot);
  const lastKey = normPath.substring(lastDot + 1);
  return { parentPath: parentPath === '' ? '$' : parentPath, lastKey };
}

function pgsqlExtractText(quotedField: string, path: string): string {
  return `${quotedField} #>> '${jsonPathToPgArray(path).replace(/'/g, "''")}'`;
}

function pgsqlExtractJson(quotedField: string, path: string): string {
  return `${quotedField} #> '${jsonPathToPgArray(path).replace(/'/g, "''")}'`;
}

function mysqlExtractText(quotedField: string, path: string): string {
  return `JSON_UNQUOTE(JSON_EXTRACT(${quotedField}, ${quotePathLiteral(path, 'mysql')}))`;
}

function mssqlExtractText(quotedField: string, path: string): string {
  return `JSON_VALUE(${quotedField}, ${quotePathLiteral(path, 'mssql')})`;
}

function oracleExtractText(quotedField: string, path: string): string {
  return `JSON_VALUE(${quotedField}, ${quotePathLiteral(path)})`;
}

function sqliteExtractText(quotedField: string, path: string): string {
  // SQLite's `json_extract` returns the JSON value in its native storage
  // class — integer for `{"a":1}`, text for `{"a":"1"}`. Every other engine's
  // extract helper (PG `#>>`, MySQL `JSON_UNQUOTE(JSON_EXTRACT)`, MSSQL/Oracle
  // `JSON_VALUE`) returns text unconditionally. Wrap in `CAST AS TEXT` so the
  // function's contract — "returns text on every engine" — holds for SQLite
  // too. Without this, equality comparisons against text literals (`= '1'`)
  // silently miss integer-typed JSON values on SQLite. Callers that need
  // numeric comparisons still wrap via `castNumericSql`; the extra
  // `CAST(CAST(... AS TEXT) AS REAL)` collapses cleanly at execution.
  return `CAST(json_extract(${quotedField}, ${quotePathLiteral(path)}) AS TEXT)`;
}

function pathExistsSql(
  engine: DatabaseEngineId,
  quotedField: string,
  path: string,
  negate: boolean,
): string {
  switch (engine) {
    case 'pgsql':
      return negate
        ? `${pgsqlExtractJson(quotedField, path)} IS NULL`
        : `${pgsqlExtractJson(quotedField, path)} IS NOT NULL`;
    case 'mysql':
      return negate
        ? `JSON_CONTAINS_PATH(${quotedField}, 'one', ${quotePathLiteral(path, 'mysql')}) = 0`
        : `JSON_CONTAINS_PATH(${quotedField}, 'one', ${quotePathLiteral(path, 'mysql')}) = 1`;
    case 'mssql': {
      // T-SQL pre-2022 has no `JSON_PATH_EXISTS` built-in. The naïve idiom
      // `(JSON_QUERY(...) IS NOT NULL OR JSON_VALUE(...) IS NOT NULL)` mis-
      // classifies `{"a":null}` as "key missing" because `JSON_QUERY` returns
      // SQL NULL for scalar values (including JSON null) and `JSON_VALUE`
      // returns SQL NULL for JSON null — there's no way to tell that branch
      // apart from "path absent" via those functions alone.
      //
      // `OPENJSON` enumerates the parent object's top-level keys (one row per
      // key) with the key name exposed in the `[key]` column. Splitting the
      // path into parent + last segment and probing for the last segment in
      // `OPENJSON(col, '<parent>')` distinguishes the two: `{"a":null}`
      // yields one row with `[key]='a'`, `{}` yields zero. PG / Oracle
      // distinguish natively (`jsonb #>` vs `JSON_EXISTS`); this MSSQL emit
      // mirrors that semantic.
      //
      // The `$` root case keeps the legacy emit — there's no "last key" to
      // enumerate, and the root-path semantic is "column has JSON content"
      // (used by the path-peek pass-through filter with empty path).
      const norm = normalizeJsonPath(path);
      if (norm === '$') {
        const lit = quotePathLiteral(path, 'mssql');
        return negate
          ? `(JSON_QUERY(${quotedField}, ${lit}) IS NULL AND JSON_VALUE(${quotedField}, ${lit}) IS NULL)`
          : `(JSON_QUERY(${quotedField}, ${lit}) IS NOT NULL OR JSON_VALUE(${quotedField}, ${lit}) IS NOT NULL)`;
      }
      const { parentPath, lastKey } = splitJsonPathLastSegment(norm);
      const parentLit = quotePathLiteral(parentPath, 'mssql');
      const keyLit = withSqlServerNationalPathPrefix(`'${lastKey.replace(/'/g, "''")}'`, lastKey);
      const inner = `EXISTS (SELECT 1 FROM OPENJSON(${quotedField}, ${parentLit}) WHERE [key] = ${keyLit})`;
      return negate ? `NOT ${inner}` : inner;
    }
    case 'oracle':
      return negate
        ? `NOT JSON_EXISTS(${quotedField}, ${quotePathLiteral(path)})`
        : `JSON_EXISTS(${quotedField}, ${quotePathLiteral(path)})`;
    case 'sqlite':
      return negate
        ? `${sqliteExtractText(quotedField, path)} IS NULL`
        : `${sqliteExtractText(quotedField, path)} IS NOT NULL`;
    case 'mock':
      return `${quotedField} JSON_HAS_PATH(${quotePathLiteral(path)})${negate ? ' = 0' : ' = 1'}`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'path-exists', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'path-exists', 'JSON filtering is not supported for azurestorage columns');
  }
}

/**
 * Engine-aware "extract this JSONPath as a text scalar" expression. Exported
 * so non-JSON-operator code paths (e.g. the `lookup` operator on a loose-FK
 * with `sourcePath`) can emit identical extract semantics — the source-of-
 * truth comparison shape mirrors `path_eq` so the user gets the same row set
 * either way.
 */
export function extractTextSql(engine: DatabaseEngineId, quotedField: string, path: string): string {
  switch (engine) {
    case 'pgsql':
      return pgsqlExtractText(quotedField, path);
    case 'mysql':
      return mysqlExtractText(quotedField, path);
    case 'mssql':
      return mssqlExtractText(quotedField, path);
    case 'oracle':
      return oracleExtractText(quotedField, path);
    case 'sqlite':
      return sqliteExtractText(quotedField, path);
    case 'mock':
      return `JSON_EXTRACT(${quotedField}, ${quotePathLiteral(path, 'mysql')})`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'extract-text', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'extract-text', 'JSON filtering is not supported for azurestorage columns');
  }
}

function castNumericSql(engine: DatabaseEngineId, textExpr: string): string {
  switch (engine) {
    case 'pgsql':
      return `(${textExpr})::numeric`;
    case 'mysql':
      return `CAST(${textExpr} AS DECIMAL(38,10))`;
    case 'mssql':
      return `TRY_CAST(${textExpr} AS DECIMAL(38,10))`;
    case 'oracle':
      return `TO_NUMBER(${textExpr})`;
    case 'sqlite':
      return `CAST(${textExpr} AS REAL)`;
    case 'mock':
      return `CAST(${textExpr} AS NUMERIC)`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'cast-numeric', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'cast-numeric', 'JSON filtering is not supported for azurestorage columns');
  }
}

// Engine-specific `is_empty` predicates. Whitespace-formatted JSON (`'{ }'`,
// `'[ ]'`, `' {} '`) must match the same as canonical empties. Approaches:
//
// - **PG**: `jsonb_typeof` + `'{}'::jsonb` cast normalizes whitespace (PG
//   parses the literal during the cast).
// - **MySQL**: `JSON_LENGTH` is whitespace-agnostic by spec.
// - **MSSQL**: `OPENJSON` parses the column and yields one row per top-level
//   key/element; an empty object / empty array (with or without whitespace)
//   yields zero rows. NULL columns are excluded explicitly because
//   `OPENJSON(NULL)` also returns 0 rows and would false-positive without
//   the `IS NOT NULL` guard. For declared-on-text the surrounding `wrap` adds
//   `ISJSON(col) = 1` so OPENJSON only sees parseable input.
// - **Oracle**: typed predicate gated by `JSON_VALUE(col, '$.type()')`. The
//   object arm uses `JSON_EXISTS(col, '$.*')` (lax `$.*` returns FALSE on
//   `{}` and TRUE on `{a:…}`); the array arm uses `JSON_EXISTS(col, '$[0]')`
//   gated by `type='array'` so the lax-mode auto-wrap (which makes `$[0]`
//   return TRUE on `{}`) cannot leak across — by the time we test `$[0]`
//   we already know the value is an array. Works on native `JSON` columns
//   (which serialize to opaque OSON — a `JSON_QUERY(col,'$') IN ('{}','[]')`
//   comparison fails silently there) and on declared-on-text CLOB. Also
//   whitespace-tolerant by construction: Oracle parses `'{ }'` / `'[ ]'`
//   as the corresponding empty container, so `JSON_VALUE(...,'.type()')`
//   answers `'object'` / `'array'` and the `JSON_EXISTS` branch returns FALSE.
//   No document-size limit (we never materialize the whole value).
//   (The earlier `JSON_QUERY(col,'$') IN ('{}','[]')` shape only worked for
//   declared-on-text CLOB — on native `JSON` Oracle returns OSON-padded text
//   whose equality with the empty-container literals never matches.)
// - **SQLite**: typed predicate. `json_array_length` returns 0 for any
//   non-array, so the array branch must gate on `json_type = 'array'`. For
//   objects, `json_each` yields one row per key — `(SELECT COUNT(*) FROM
//   json_each(col)) = 0` is the empty-object check. `json_type(NULL)` is
//   NULL so NULL columns evaluate to NULL and are excluded.
function isEmptySql(engine: DatabaseEngineId, quotedField: string): string {
  switch (engine) {
    case 'pgsql':
      return (
        `((jsonb_typeof(${quotedField}) = 'object' AND ${quotedField} = '{}'::jsonb) ` +
        `OR (jsonb_typeof(${quotedField}) = 'array' AND jsonb_array_length(${quotedField}) = 0))`
      );
    case 'mysql':
      return `JSON_LENGTH(${quotedField}) = 0`;
    case 'mssql':
      return `(${quotedField} IS NOT NULL AND (SELECT COUNT(*) FROM OPENJSON(${quotedField})) = 0)`;
    case 'oracle':
      return (
        `((JSON_VALUE(${quotedField}, '$.type()') = 'object' AND NOT JSON_EXISTS(${quotedField}, '$.*')) ` +
        `OR (JSON_VALUE(${quotedField}, '$.type()') = 'array' AND NOT JSON_EXISTS(${quotedField}, '$[0]')))`
      );
    case 'sqlite':
      return (
        `((json_type(${quotedField}) = 'array' AND json_array_length(${quotedField}) = 0) ` +
        `OR (json_type(${quotedField}) = 'object' AND (SELECT COUNT(*) FROM json_each(${quotedField})) = 0))`
      );
    case 'mock':
      return `JSON_LENGTH(${quotedField}) = 0`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'is-empty', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'is-empty', 'JSON filtering is not supported for azurestorage columns');
  }
}

// `is_not_empty` is NOT a plain NOT of `is_empty` — that formulation leaks
// NULL rows on MSSQL (the explicit `col IS NOT NULL AND COUNT=0` inverts to
// `col IS NULL OR COUNT>0`, which lets NULL through) and over-matches scalars
// or non-container values on engines whose `is_empty` is type-narrowed.
// Mirror `isEmptySql`'s structure with reversed comparisons so we match only
// non-empty objects / arrays — never NULL, never scalars, never invalid JSON.
function isNotEmptySql(engine: DatabaseEngineId, quotedField: string): string {
  switch (engine) {
    case 'pgsql':
      return (
        `((jsonb_typeof(${quotedField}) = 'object' AND ${quotedField} <> '{}'::jsonb) ` +
        `OR (jsonb_typeof(${quotedField}) = 'array' AND jsonb_array_length(${quotedField}) > 0))`
      );
    case 'mysql':
      return `JSON_LENGTH(${quotedField}) > 0`;
    case 'mssql':
      return `(${quotedField} IS NOT NULL AND (SELECT COUNT(*) FROM OPENJSON(${quotedField})) > 0)`;
    case 'oracle':
      // Mirror `isEmptySql`'s typed-predicate shape with the JSON_EXISTS
      // branches flipped to "present" (no NOT). Scalars / NULL columns
      // produce a NULL `type()` which fails both arms → row excluded.
      return (
        `((JSON_VALUE(${quotedField}, '$.type()') = 'object' AND JSON_EXISTS(${quotedField}, '$.*')) ` +
        `OR (JSON_VALUE(${quotedField}, '$.type()') = 'array' AND JSON_EXISTS(${quotedField}, '$[0]')))`
      );
    case 'sqlite':
      return (
        `((json_type(${quotedField}) = 'array' AND json_array_length(${quotedField}) > 0) ` +
        `OR (json_type(${quotedField}) = 'object' AND (SELECT COUNT(*) FROM json_each(${quotedField})) > 0))`
      );
    case 'mock':
      return `JSON_LENGTH(${quotedField}) > 0`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'is-not-empty', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'is-not-empty', 'JSON filtering is not supported for azurestorage columns');
  }
}

function isValidSql(
  engine: DatabaseEngineId,
  quotedField: string,
  declaredOnText: boolean,
  negate: boolean,
): string {
  // Native JSON columns are always valid by storage; only declared-on-text
  // columns can hold an invalid string.
  if (!declaredOnText) {
    return negate ? `1 = 0` : `${quotedField} IS NOT NULL`;
  }
  switch (engine) {
    case 'mssql':
      return `ISJSON(${quotedField}) = ${negate ? 0 : 1}`;
    case 'oracle':
      return negate ? `${quotedField} IS NOT JSON` : `${quotedField} IS JSON`;
    case 'sqlite':
      return `json_valid(${quotedField}) = ${negate ? 0 : 1}`;
    case 'mysql':
      return `JSON_VALID(${quotedField}) = ${negate ? 0 : 1}`;
    case 'pgsql':
      // PG 16+ `IS JSON` predicate. Older servers error on this — gated at
      // the UI / declare-dialog level, not here.
      return negate ? `${quotedField} IS NOT JSON` : `${quotedField} IS JSON`;
    case 'mock':
      return negate ? `${quotedField} IS NULL` : `${quotedField} IS NOT NULL`;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'is-valid', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'is-valid', 'JSON filtering is not supported for azurestorage columns');
  }
}

/**
 * Build a per-engine lateral subquery body — a SQL fragment that walks the
 * matches of a wildcard path and exposes each match's text via an alias `v`.
 * Returns `{ from, valueExpr }` so callers can compose
 * `EXISTS (SELECT 1 FROM <from> WHERE <predicate-on-valueExpr>)`.
 *
 * - `valueExpr` is engine-specific text-coerced expression for the matched
 *   element; matches the non-wildcard `extractTextSql` semantics so the
 *   surrounding predicate (= / <> / LIKE) reads identically.
 * - For PG / MySQL / Oracle the full path is passed natively (their
 *   JSONPath functions and `JSON_TABLE` row-paths walk multi-wildcards
 *   on their own).
 * - MSSQL / SQLite split at the first `[*]` and walk only one level via
 *   `OPENJSON` / `json_each`; multi-wildcard paths beyond the first `[*]`
 *   are unsupported on those engines (documented in the public docs).
 */
function wildcardLateral(
  engine: DatabaseEngineId,
  quotedField: string,
  path: string,
): { from: string; valueExpr: string } {
  const fullLit = quotePathLiteral(path);
  switch (engine) {
    case 'pgsql':
      return {
        from: `jsonb_path_query(${quotedField}, ${fullLit}) AS p`,
        valueExpr: `(p #>> '{}')`,
      };
    case 'mysql': {
      // MySQL's `JSON_TABLE` row_path needs to iterate the array — passing
      // a non-wildcard path returns the whole value as a single row. Split
      // at the first `[*]` and reattach the wildcard so the row_path is
      // `<rowPath>[*]`; the column path is the tail.
      //
      // Column type VARCHAR(4000) coerces each match to a SQL string for
      // the surrounding text-comparison predicate. JSON-type + JSON_UNQUOTE
      // round-trips JSON integers in a way that compared incorrectly to a
      // numeric SQL literal under JSON_TABLE; VARCHAR coerces both sides
      // to text and matches the non-wildcard `extractTextSql` shape.
      // CHAR is capped at 255 in MySQL so VARCHAR is the right type for
      // the longer JSON-string ceiling.
      const { rowPath, tailPath } = splitFirstWildcard(path);
      if (/[\\\0]/u.test(path)) {
        // JSON_TABLE requires literal paths; move user paths into JSON_EXTRACT.
        return {
          from: `JSON_TABLE(JSON_EXTRACT(${quotedField}, ${quotePathLiteral(rowPath, 'mysql')}), '$[*]' COLUMNS (v JSON PATH '$')) AS jt`,
          valueExpr: `JSON_UNQUOTE(JSON_EXTRACT(jt.v, ${quotePathLiteral(tailPath, 'mysql')}))`,
        };
      }
      const arrayPathLit = quotePathLiteral(`${rowPath === '$' ? '$' : rowPath}[*]`);
      return {
        from: `JSON_TABLE(${quotedField}, ${arrayPathLit} COLUMNS (v VARCHAR(4000) PATH ${quotePathLiteral(tailPath)})) AS jt`,
        valueExpr: `jt.v`,
      };
    }
    case 'oracle': {
      // Oracle `JSON_TABLE` mirrors MySQL — iterate the array via a
      // wildcard row_path and use the tail as the column path.
      const { rowPath, tailPath } = splitFirstWildcard(path);
      const arrayPathLit = quotePathLiteral(`${rowPath === '$' ? '$' : rowPath}[*]`);
      return {
        from: `JSON_TABLE(${quotedField}, ${arrayPathLit} COLUMNS (v VARCHAR2(4000) PATH ${quotePathLiteral(tailPath)})) jt`,
        valueExpr: `jt.v`,
      };
    }
    case 'mssql': {
      const { rowPath, tailPath } = splitFirstWildcard(path);
      const rowLit = quotePathLiteral(rowPath, 'mssql');
      // OPENJSON without a WITH clause exposes [key], [value], [type]. The
      // `value` column is already an nvarchar(max) holding the element's
      // text representation — for scalar array elements (e.g. `["a","b"]`)
      // it's the unquoted scalar, NOT a JSON-encoded scalar. That means
      // `JSON_VALUE(j.[value], '$')` returns NULL on scalar element walks
      // (the input isn't a valid JSON document), so when tailPath === '$'
      // we use `j.[value]` directly. Mirrors the SQLite branch below.
      const valueExpr =
        tailPath === '$'
          ? 'j.[value]'
          : `JSON_VALUE(j.[value], ${quotePathLiteral(tailPath, 'mssql')})`;
      return {
        from: `OPENJSON(${quotedField}, ${rowLit}) AS j`,
        valueExpr,
      };
    }
    case 'sqlite': {
      const { rowPath, tailPath } = splitFirstWildcard(path);
      const rowLit = quotePathLiteral(rowPath);
      // `json_each.value` and `json_extract` both return the JSON value in its
      // native storage class on SQLite (integer/real/text). Wrap in `CAST AS
      // TEXT` so equality comparisons against text literals (`= '1'`) match
      // integer-typed array elements too — same rationale as `sqliteExtractText`.
      const valueExpr =
        tailPath === '$'
          ? 'CAST(je.value AS TEXT)'
          : `CAST(json_extract(je.value, ${quotePathLiteral(tailPath)}) AS TEXT)`;
      return {
        from: `json_each(${quotedField}, ${rowLit}) AS je`,
        valueExpr,
      };
    }
    case 'mock':
      return {
        from: `JSON_WALK(${quotedField}, ${fullLit}) AS p`,
        valueExpr: 'p.value',
      };
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'wildcard-lateral', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'wildcard-lateral', 'JSON filtering is not supported for azurestorage columns');
  }
}

/**
 * Build the engine's native "is there at least one match for this wildcard
 * path?" predicate. Used for `has_property` (positive) and inverted for
 * `not_has_property`.
 */
function wildcardPathExistsSql(
  engine: DatabaseEngineId,
  quotedField: string,
  path: string,
  negate: boolean,
): string {
  const lit = quotePathLiteral(path, engine);
  switch (engine) {
    case 'pgsql':
      return negate
        ? `NOT jsonb_path_exists(${quotedField}, ${lit})`
        : `jsonb_path_exists(${quotedField}, ${lit})`;
    case 'mysql':
      return `JSON_CONTAINS_PATH(${quotedField}, 'one', ${lit}) = ${negate ? 0 : 1}`;
    case 'oracle':
      return negate
        ? `NOT JSON_EXISTS(${quotedField}, ${lit})`
        : `JSON_EXISTS(${quotedField}, ${lit})`;
    case 'mssql':
    case 'sqlite':
    case 'mock': {
      // No native wildcard-path-exists; use the lateral walk wrapped in
      // EXISTS. (MySQL has `JSON_CONTAINS_PATH` so it doesn't reach here.)
      const lat = wildcardLateral(engine, quotedField, path);
      const inner = `EXISTS (SELECT 1 FROM ${lat.from})`;
      return negate ? `NOT ${inner}` : inner;
    }
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'wildcard-path-exists', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'wildcard-path-exists', 'JSON filtering is not supported for azurestorage columns');
  }
}

export function validityGuardSql(engine: DatabaseEngineId, quotedField: string): string | null {
  switch (engine) {
    case 'mssql':
      return `ISJSON(${quotedField}) = 1`;
    case 'oracle':
      return `${quotedField} IS JSON`;
    case 'sqlite':
      return `json_valid(${quotedField}) = 1`;
    case 'mysql':
      // MySQL JSON functions error on invalid JSON text. Without this guard,
      // a single bad row in a declared-on-text `varchar` would error the
      // whole query.
      return `JSON_VALID(${quotedField}) = 1`;
    case 'pgsql':
      // PG declared-on-text uses a CASE-wrapped jsonb expression instead of
      // an AND-style validity guard (see `coerceForDeclaredOnText`). The
      // CASE returns NULL for invalid JSON, which propagates through the
      // surrounding predicate so bad rows are excluded — same effect as a
      // guard, but immune to PG's planner reordering of `AND` clauses.
      return null;
    case 'mock':
      return null;
    case 'kusto':
      throw new JsonFilterUnsupportedError(engine, 'validity-guard', 'JSON filtering is not supported for kusto columns');
    case 'azurestorage':
      throw new JsonFilterUnsupportedError(engine, 'validity-guard', 'JSON filtering is not supported for azurestorage columns');
  }
}

/**
 * Produce a jsonb-typed expression for a column on PG so downstream operators
 * (`#>`, `#>>`, `jsonb_path_exists`, …) always see a jsonb LHS, regardless of
 * whether the underlying column is `jsonb`, `json`, or text.
 *
 * - `declaredOnText`: wrap with `(CASE WHEN col IS JSON THEN (col)::jsonb END)`.
 *   The CASE is required (not just a `col IS JSON AND ...` guard) because PG's
 *   planner doesn't guarantee left-to-right `AND` evaluation — it can attempt
 *   the `::jsonb` cast on a non-JSON row and error. CASE short-circuits, and
 *   evaluates to NULL on invalid rows, which propagates through predicates and
 *   excludes them from positive matches.
 * - Otherwise: `(col)::jsonb` — no-op on native `jsonb`, validates+converts on
 *   `json`, parses on `text`/`varchar`. The cast is also a safety net for
 *   filters whose `jsonDeclaredOnText` flag was not stamped by the UI on a
 *   text-typed column with valid JSON values; without it the SQL would error
 *   with `operator does not exist: text #>>`.
 *
 * Returns `quotedField` unchanged on engines other than PG (those rely on
 * `validityGuardSql` to AND-prepend a guard for declared-on-text columns and
 * native operators do not require a cast).
 */
export function coerceForDeclaredOnText(
  engine: DatabaseEngineId,
  quotedField: string,
  declaredOnText: boolean,
): string {
  if (engine !== 'pgsql') return quotedField;
  if (declaredOnText) {
    return `(CASE WHEN ${quotedField} IS JSON THEN (${quotedField})::jsonb END)`;
  }
  return `(${quotedField})::jsonb`;
}

/**
 * Build a SQL fragment for a `json`-operator filter condition.
 *
 * `condition.operator` MUST be `'json'`; callers that route generically
 * through `buildConditionSql` should branch before calling this.
 */
export function buildJsonConditionSql(
  quotedField: string,
  condition: FilterCondition,
  engine: DatabaseEngineId,
  quoteValue: (v: unknown) => string,
): string {
  // SQL Server: the JSON functions return nvarchar — a text value with a
  // character outside ASCII is written `N'…'` (`sqlServerTextLiteral.ts`).
  const escapeValueFn = engine === 'mssql'
    ? (v: unknown) => withSqlServerNationalPrefix(quoteValue(v), v)
    : quoteValue;
  const sub: JsonSubOperator = condition.jsonSubOperator ?? 'has_property';
  const path = condition.path ?? '$';
  const declaredOnText = condition.jsonDeclaredOnText === true;
  const wildcard = hasJsonPathWildcard(path);
  const recursive = hasJsonRecursiveDescent(path);
  // `coerceForDeclaredOnText` returns a jsonb-typed expression on PG (CASE
  // wrap for declared-on-text, `(col)::jsonb` otherwise) and the raw column
  // on other engines. Call sites that need a jsonb-typed value (`#>>`, `#>`,
  // `jsonb_path_exists`, `jsonb_path_query`, `jsonb_typeof`, …) all use
  // `coercedField`; on non-PG engines the underlying type is already json or
  // the engine-specific helper accepts text directly.
  const coercedField = coerceForDeclaredOnText(engine, quotedField, declaredOnText);

  // Slice 13: `..` is supported only on PG and Oracle. On MySQL / MSSQL /
  // SQLite the SQL emit throws — the filter chip catches and renders the
  // engine-specific inline error. The validator surfaces the same hint
  // pre-apply so well-behaved users never reach this branch.
  if (recursive && !RECURSIVE_DESCENT_SUPPORTED_ENGINES.includes(engine)) {
    throw new JsonFilterUnsupportedError(
      engine,
      'recursive-descent',
      'Recursive descent (..) not supported on this engine',
    );
  }

  const wrap = (predicate: string): string => {
    if (!declaredOnText) return predicate;
    if (VALIDITY_SUB_OPS.includes(sub)) return predicate;
    const guard = validityGuardSql(engine, quotedField);
    return guard ? `${guard} AND ${predicate}` : predicate;
  };

  const numericPathOp = (op: '>' | '>=' | '<' | '<=') => {
    const text = extractTextSql(engine, coercedField, path);
    const numeric = castNumericSql(engine, text);
    return wrap(`${numeric} ${op} ${escapeValueFn(condition.value)}`);
  };

  // Wildcard paths walk every match via a per-engine lateral subquery and
  // wrap the comparison with the engine's "is there a match" idiom.
  //
  // PG / MSSQL / Oracle / SQLite use `EXISTS (SELECT 1 FROM <lateral>)`;
  // MySQL uses `(SELECT COUNT(*) FROM <lateral>) > 0` because EXISTS over
  // `JSON_TABLE` with the outer table's column as JSON_TABLE's first
  // argument silently returns 0 rows even when matches exist (observed
  // on MySQL 8.0.45 — a correlated-subquery + JSON_TABLE interaction).
  // The COUNT scalar-subquery form sidesteps the bug.
  //
  // Numeric comparisons over wildcard paths are deferred to Slice 14
  // (any/all aggregates) — they fall through to the scalar branch
  // below, which on a wildcard path will pick the first match per
  // engine semantics (PG / MySQL behavior) or error (MSSQL / SQLite).
  // The validator surfaces the unsupported hint while Slice 14 lands.
  const wildcardExists = (
    fromClause: string,
    whereClause: string,
    negate: boolean,
  ): string => {
    if (engine === 'mysql') {
      const inner = `(SELECT COUNT(*) FROM ${fromClause} WHERE ${whereClause})`;
      return negate ? `${inner} = 0` : `${inner} > 0`;
    }
    const inner = `EXISTS (SELECT 1 FROM ${fromClause} WHERE ${whereClause})`;
    return negate ? `NOT ${inner}` : inner;
  };
  const wildcardPredicate = (
    operator: '=' | '<>' | 'LIKE',
    valueRaw: unknown,
    negate = false,
  ): string => {
    const lat = wildcardLateral(engine, coercedField, path);
    let where: string;
    if (operator === 'LIKE') {
      where = containsLikeSql(lat.valueExpr, valueRaw, engine, escapeValueFn);
    } else {
      // `lat.valueExpr` is text on every engine — quote the value as text too
      // so the compare is text-vs-text. See `escapeAsTextLiteral` header.
      where = `${lat.valueExpr} ${operator} ${escapeAsTextLiteral(valueRaw, engine)}`;
    }
    return wildcardExists(lat.from, where, negate);
  };

  // Recursive-descent variants share the lateral-EXISTS shape with
  // wildcards but use their own per-engine path translation (PG: `**`,
  // Oracle: `..` natively). Reuse the `wildcardExists` wrapper so the
  // same `EXISTS` / `COUNT > 0` machinery applies — except recursive
  // descent never reaches MySQL (it throws above), so the EXISTS branch
  // is the only one taken here.
  const recursivePredicate = (
    operator: '=' | '<>' | 'LIKE',
    valueRaw: unknown,
    negate = false,
  ): string => {
    const lat = recursiveDescentLateral(engine, coercedField, path);
    let where: string;
    if (operator === 'LIKE') {
      where = containsLikeSql(lat.valueExpr, valueRaw, engine, escapeValueFn);
    } else {
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      where = `${lat.valueExpr} ${operator} ${escapeAsTextLiteral(valueRaw, engine)}`;
    }
    const inner = `EXISTS (SELECT 1 FROM ${lat.from} WHERE ${where})`;
    return negate ? `NOT ${inner}` : inner;
  };

  // Slice 14 — aggregate predicates over a wildcard path. `any_*` means
  // "at least one matched element satisfies"; `all_*` means "there's at
  // least one match AND every match satisfies". Both use the same
  // per-engine lateral as Slice 12 (PG `jsonb_path_query`, MySQL/Oracle
  // `JSON_TABLE`, MSSQL `OPENJSON`, SQLite `json_each`). The MySQL
  // `EXISTS`-over-`JSON_TABLE` quirk applies here too — `wildcardExists`
  // routes to the COUNT-subquery shape on MySQL automatically.
  //
  // For numeric `*_gt` / `*_gte` / `*_lt` / `*_lte`, we cast the matched
  // text via `castNumericSql`. PG's `::numeric` cast raises on non-numeric
  // input, so users with mixed-type arrays may need to combine with a
  // `path_is_not_null` / type filter — same caveat as the scalar
  // `path_gt`. Documented in the public docs.
  const aggregateAnyPredicate = (
    op: '=' | 'IN' | 'CONTAINS' | '>' | '>=' | '<' | '<=',
    valueRaw: unknown,
  ): string => {
    const lat = wildcardLateral(engine, coercedField, path);
    let whereExpr: string;
    if (op === 'CONTAINS') {
      whereExpr = containsLikeSql(lat.valueExpr, valueRaw, engine, escapeValueFn);
    } else if (op === 'IN') {
      // Multiselect enum — text-vs-text membership. See `escapeAsTextInList`.
      whereExpr = `${lat.valueExpr} IN (${escapeAsTextInList(valueRaw as unknown[], engine)})`;
    } else if (op === '=') {
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      whereExpr = `${lat.valueExpr} = ${escapeAsTextLiteral(valueRaw, engine)}`;
    } else {
      const numeric = castNumericSql(engine, lat.valueExpr);
      whereExpr = `${numeric} ${op} ${escapeValueFn(valueRaw)}`;
    }
    return wildcardExists(lat.from, whereExpr, false);
  };

  // `all_*` requires (a) the path matched at least one element AND
  // (b) no matched element violates the predicate. NULL handling: a
  // matched element whose extract is NULL is treated as a violation —
  // otherwise `all_gt 5` over `[1, null, 7]` would silently let the NULL
  // pass. The violation predicate explicitly includes `IS NULL`.
  //
  // On MySQL we use `(SELECT COUNT(*) ...) > 0 AND (... violations) = 0`
  // because `EXISTS` over `JSON_TABLE` correlated subqueries silently
  // returns zero rows on MySQL 8 (documented at the wildcardExists site).
  const aggregateAllPredicate = (
    op: '=' | 'IN' | '>' | '>=' | '<' | '<=',
    valueRaw: unknown,
  ): string => {
    const lat = wildcardLateral(engine, coercedField, path);
    let violation: string;
    if (op === 'IN') {
      // Multiselect enum — a matched element violates `all` if its extract
      // is NULL or is not one of the selected values. See `escapeAsTextInList`.
      violation = `${lat.valueExpr} IS NULL OR ${lat.valueExpr} NOT IN (${escapeAsTextInList(valueRaw as unknown[], engine)})`;
    } else if (op === '=') {
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      violation = `${lat.valueExpr} IS NULL OR ${lat.valueExpr} <> ${escapeAsTextLiteral(valueRaw, engine)}`;
    } else {
      const numeric = castNumericSql(engine, lat.valueExpr);
      const inverseOp: Record<'>' | '>=' | '<' | '<=', string> = {
        '>': '<=',
        '>=': '<',
        '<': '>=',
        '<=': '>',
      };
      violation = `${numeric} IS NULL OR ${numeric} ${inverseOp[op]} ${escapeValueFn(valueRaw)}`;
    }
    if (engine === 'mysql') {
      const total = `(SELECT COUNT(*) FROM ${lat.from})`;
      const violations = `(SELECT COUNT(*) FROM ${lat.from} WHERE ${violation})`;
      return `(${total} > 0 AND ${violations} = 0)`;
    }
    const exists = `EXISTS (SELECT 1 FROM ${lat.from})`;
    const noViolations = `NOT EXISTS (SELECT 1 FROM ${lat.from} WHERE ${violation})`;
    return `(${exists} AND ${noViolations})`;
  };

  switch (sub) {
    case 'has_property':
      if (recursive) return wrap(recursiveDescentPathExistsSql(engine, coercedField, path, false));
      if (wildcard) return wrap(wildcardPathExistsSql(engine, coercedField, path, false));
      return wrap(pathExistsSql(engine, coercedField, path, false));
    case 'not_has_property':
      if (recursive) return wrap(recursiveDescentPathExistsSql(engine, coercedField, path, true));
      if (wildcard) return wrap(wildcardPathExistsSql(engine, coercedField, path, true));
      return wrap(pathExistsSql(engine, coercedField, path, true));
    case 'path_eq':
    // `property_lookup` (Pass 3 Slice C) is a scalar equality at a path
    // sourced from the FK row picker. SQL-wise it's `path_eq` — the
    // difference is purely UX (value comes from the picker, not free text).
    case 'property_lookup': {
      if (recursive) return wrap(recursivePredicate('=', condition.value));
      if (wildcard) return wrap(wildcardPredicate('=', condition.value));
      const text = extractTextSql(engine, coercedField, path);
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      return wrap(`${text} = ${escapeAsTextLiteral(condition.value, engine)}`);
    }
    // `path_ieq` / `any_ieq` (PG19) — case-insensitive equality via the
    // jsonpath `.lower()` string method (PG19-FACTS f1/f2). pgsql-only in v1;
    // both ops are gated out of the dropdown on every other engine and on
    // pgsql < 19, so reaching this branch elsewhere is a defensive throw.
    // The `[*]` wildcard (`any_ieq`) stays inside the jsonpath — PG walks it
    // natively — so both ops share one emit that passes the full path through.
    case 'path_ieq':
    case 'any_ieq': {
      if (engine !== 'pgsql') {
        throw new JsonFilterUnsupportedError(
          engine,
          'jsonpath-methods',
          'Case-insensitive jsonpath methods (path_ieq / any_ieq) require pgsql (PostgreSQL 19+)',
        );
      }
      const lowered = String(condition.value ?? '').toLowerCase();
      return wrap(`jsonb_path_exists(${coercedField}, ${jsonpathLowerEqLiteral(path, lowered)})`);
    }
    // `property_enum` is a scalar-path predicate sourced from the enum picker:
    // a single selected value emits `= literal` (identical to `property_lookup`);
    // a multiselect emits `IN (...)`; an empty multiselect is a no-op false
    // predicate (mirrors `property_lookup` UX, swapping the picker source).
    case 'property_enum': {
      const text = extractTextSql(engine, coercedField, path);
      if (Array.isArray(condition.value)) {
        if (condition.value.length === 0) return wrap('1 = 0');
        // Text-vs-text membership — see `escapeAsTextInList`.
        return wrap(`${text} IN (${escapeAsTextInList(condition.value, engine)})`);
      }
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      return wrap(`${text} = ${escapeAsTextLiteral(condition.value, engine)}`);
    }
    case 'path_neq': {
      // Missing-path safety: `JSON_VALUE` / `#>>` return SQL NULL when the
      // path is absent, and `NULL <> 'x'` is NULL (not TRUE), so a naïve
      // `<>` would silently drop every row that lacks the property —
      // opposite of "not equal to x" intent. Include the IS NULL branch.
      if (recursive) {
        // Mirror wildcard semantic: rows where NO match equals the
        // comparand (including zero matches) → `NOT EXISTS(... WHERE v = value)`.
        return wrap(recursivePredicate('=', condition.value, true));
      }
      if (wildcard) {
        // Wildcard `path_neq` mirrors the H1 semantic: rows where NO
        // matched element equals the comparand match — including rows
        // where the wildcard yields zero matches at all (path absent).
        // Equivalent to `NOT EXISTS(... WHERE v = value)`.
        return wrap(wildcardPredicate('=', condition.value, true));
      }
      const text = extractTextSql(engine, coercedField, path);
      // Text-vs-text compare — see `escapeAsTextLiteral` header.
      return wrap(`(${text} IS NULL OR ${text} <> ${escapeAsTextLiteral(condition.value, engine)})`);
    }
    case 'path_is_null': {
      const text = extractTextSql(engine, coercedField, path);
      return wrap(`${text} IS NULL`);
    }
    case 'path_is_not_null': {
      const text = extractTextSql(engine, coercedField, path);
      return wrap(`${text} IS NOT NULL`);
    }
    case 'path_contains': {
      if (recursive) return wrap(recursivePredicate('LIKE', condition.value));
      if (wildcard) return wrap(wildcardPredicate('LIKE', condition.value));
      const text = extractTextSql(engine, coercedField, path);
      // `100%` searches for the literal `100%` (`containsLikeSql`).
      return wrap(containsLikeSql(text, condition.value, engine, escapeValueFn));
    }
    case 'path_gt':
      return numericPathOp('>');
    case 'path_gte':
      return numericPathOp('>=');
    case 'path_lt':
      return numericPathOp('<');
    case 'path_lte':
      return numericPathOp('<=');
    case 'is_empty':
      return wrap(isEmptySql(engine, coercedField));
    case 'is_not_empty':
      return wrap(isNotEmptySql(engine, coercedField));
    case 'is_valid':
      return isValidSql(engine, quotedField, declaredOnText, false);
    case 'is_not_valid':
      return isValidSql(engine, quotedField, declaredOnText, true);
    case 'any_eq':
    // `any_lookup` (Pass 3 Slice C) is `any_eq` whose value comes from the
    // FK row picker. Mirrors the wildcard-aggregate emit.
    case 'any_lookup':
      return wrap(aggregateAnyPredicate('=', condition.value));
    // `any_enum` — at least one matched element is the selected value (scalar)
    // or one of the selected values (multiselect). Empty multiselect → no-op.
    case 'any_enum':
      if (Array.isArray(condition.value) && condition.value.length === 0) return wrap('1 = 0');
      return wrap(
        aggregateAnyPredicate(Array.isArray(condition.value) ? 'IN' : '=', condition.value),
      );
    case 'any_contains':
      return wrap(aggregateAnyPredicate('CONTAINS', condition.value));
    case 'any_gt':
      return wrap(aggregateAnyPredicate('>', condition.value));
    case 'any_gte':
      return wrap(aggregateAnyPredicate('>=', condition.value));
    case 'any_lt':
      return wrap(aggregateAnyPredicate('<', condition.value));
    case 'any_lte':
      return wrap(aggregateAnyPredicate('<=', condition.value));
    case 'all_eq':
    // `all_lookup` (Pass 3 Slice C) is `all_eq` whose value comes from the
    // FK row picker. Empty arrays don't match (matches `all_eq` semantics).
    case 'all_lookup':
      return wrap(aggregateAllPredicate('=', condition.value));
    // `all_enum` — every matched element is the selected value (scalar) or one
    // of the selected values (multiselect); requires ≥1 match. Empty
    // multiselect → no-op (matches `all_lookup` empty-array semantics).
    case 'all_enum':
      if (Array.isArray(condition.value) && condition.value.length === 0) return wrap('1 = 0');
      return wrap(
        aggregateAllPredicate(Array.isArray(condition.value) ? 'IN' : '=', condition.value),
      );
    case 'all_gt':
      return wrap(aggregateAllPredicate('>', condition.value));
    case 'all_gte':
      return wrap(aggregateAllPredicate('>=', condition.value));
    case 'all_lt':
      return wrap(aggregateAllPredicate('<', condition.value));
    case 'all_lte':
      return wrap(aggregateAllPredicate('<=', condition.value));
  }
}
